import Dexie from 'dexie'
import { toChecksumAddress } from 'web3-utils'
import { SUPPORTED_VERIFICATION_METHOD_STRINGS } from '@erc725/erc725.js/build/main/src/constants/constants'

import { AuthenticatedApp } from '@/shared/models/AuthenticatedApp'
import {
  BackgroundProfile,
  DigitalAsset,
  LastPopupState,
  PopupProfile,
  SeenPermissionChanges,
} from '@/shared/models/Profile'
import { Sign } from '@/shared/models/Sign'
import { Transaction } from '@/shared/models/Transaction'
import { Origin } from '@/shared/models/Origin'
import { TransactionEvent } from '@/shared/models/TransactionEvent'
import { Restriction } from '@/shared/models/Restriction'
import { AppNotification } from '@/shared/models/AppNotification'
import { DbConfiguration } from '@/shared/models/DbConfiguration'
import { ControllerCustomName } from '../../models/ControllerCustomName'
import { Controller } from '../../models/Controller'
import { SomethingWentWrongError } from '../../models/SomethingWentWrongError'
import { DexieSchemaWithChecksum } from '../DexieSchemaWithChecksum'

type DexieTable<T, K> = Dexie.Table<T, K>

const ALLOWED_CALLS_SCHEMA =
  '[type+profileAddress+appAddress+address], profileAddress'

const UP_BROWSER_EXTENSION = 'UP Browser Extension'

export type DexieModels = {
  lastPopupState: LastPopupState
  profiles: BackgroundProfile
  popupProfiles: PopupProfile
  notifications: AppNotification
  transactions: Transaction
  transaction_events: TransactionEvent
  signs: Sign
  authenticated_apps: AuthenticatedApp
  allowed_calls: Restriction
  origins: Origin
  configuration: DbConfiguration
  hasSeenPermissionChangeWarning: SeenPermissionChanges
  digitalAssets: DigitalAsset
  controllers: Controller
  controllerCustomNames: ControllerCustomName
  somethingWentWrongErrors: SomethingWentWrongError
}

// Current schema version: 20
// {
//   lastPopupState: 'profileAddress',
//   hasSeenPermissionChangeWarning: 'profileAddress',
//   profiles: 'address',
//   popupProfiles: 'address',
//   digitalAssets: 'assetAddress',
//   notifications: '++id, profileAddress, type',
//   transactions: 'requestId, transactionHash, createdBy',
//   transaction_events: '++id',
//   signs: 'profileAddress',
//   authenticated_apps: '[origin+profileAddress], origin, profileAddress',
//   allowed_calls: ALLOWED_CALLS_SCHEMA,
//   origins: 'url',
//   configuration: 'id',
//   controllers: '[controllerAddress+profileAddress],profileAddress',
//   controllerCustomNames: '[controllerAddress+profileAddress]',
// }

export class NetworkSpecificSchema extends DexieSchemaWithChecksum {
  lastPopupState!: DexieTable<LastPopupState, string>
  hasSeenPermissionChangeWarning!: DexieTable<SeenPermissionChanges, string>
  digitalAssets!: DexieTable<DigitalAsset, string>
  profiles!: DexieTable<BackgroundProfile, string>
  popupProfiles!: DexieTable<PopupProfile, string>
  notifications!: DexieTable<AppNotification, string>
  transactions!: DexieTable<Transaction, number>
  transaction_events!: DexieTable<TransactionEvent, string>
  signs!: DexieTable<Sign, string>
  authenticated_apps!: DexieTable<AuthenticatedApp, string>
  allowed_calls!: DexieTable<Restriction, string>
  origins!: DexieTable<Origin, string>
  configuration!: DexieTable<DbConfiguration, string>
  controllers!: DexieTable<Controller, string>
  controllerCustomNames!: DexieTable<ControllerCustomName, string>
  somethingWentWrongErrors!: DexieTable<SomethingWentWrongError, number>

  /**
   * Creates a new instance of the database with the given Network ID.
   * @param networkID The Network ID to use for the database name.
   */
  constructor(networkID: string) {
    super(networkID)
    this.useValidateChecksumsMiddleware()
    this.executeMigrations()
  }

  executeMigrations() {
    this.version(1).stores({
      lastPopupState: 'profileAddress',
      profiles: 'address',
      popupProfiles: 'address',
      notifications: '++id, profileAddress, type',
      transactions: 'requestId, transactionHash, createdBy',
      transaction_events: '++id',
      signs: 'profileAddress',
      authenticated_apps: '[origin+profileAddress], origin, profileAddress',
      authorized_apps: '[address+profileAddress], profileAddress',
      allowed_addresses: '[type+profileAddress+appAddress], profileAddress',
    })
    this.version(2).upgrade(tx => {
      return tx
        .table('popupProfiles')
        .toCollection()
        .modify(tx => {
          tx.isOwner = true
        })
    })
    this.version(4)
      .stores({
        transactions:
          'requestId, transactionHash, createdBy, [createdBy+status+executeMethod]',
      })
      .upgrade(tx => {
        return tx
          .table('profiles')
          .toCollection()
          .modify(tx => {
            delete tx['keyManagerAddress']
            delete tx['universalReceiverAddress']
          })
      })
      .upgrade(tx => {
        return tx
          .table('authorized_apps')
          .toCollection()
          .modify(tx => {
            tx['address'] = toChecksumAddress(tx['address'])
            delete tx['keyManagerAddress']
            delete tx['universalReceiverAddress']
          })
      })
    this.version(5).stores({
      origins: 'url',
    })
    this.version(6).stores({
      configuration: 'id',
    })
    this.version(7).stores({
      transactions:
        'requestId, transactionHash, createdBy, [createdBy+status+executeMethod], relayerId',
    })

    this.version(8).upgrade(tx => {
      return tx
        .table('popupProfiles')
        .toCollection()
        .modify(tx => {
          tx.createdAt = Date.now()
        })
    })

    this.version(9).upgrade(tx => {
      return tx
        .table('transactions')
        .toCollection()
        .modify(tx => {
          tx.gasProvider = tx.relayerId
            ? { id: tx.relayerId, type: 'relayer' }
            : { id: '', type: 'controller' }
          delete tx['relayerId']
        })
    })

    this.version(10).upgrade(tx => {
      return tx
        .table('popupProfiles')
        .toCollection()
        .modify(tx => {
          tx['saved']['gasProviderId'] = tx['saved']['defaultRelayerId']
          delete tx['saved']['defaultRelayerId']
        })
    })

    this.version(11).upgrade(async tx => {
      for (const tableName of tx.storeNames) {
        const table = tx.table(tableName)
        await table
          .toCollection()
          .modify((tx: any) => (tx = this.writeChecksum(tx, tableName)))
      }
    })
    this.version(12)
      .stores({
        allowed_calls: ALLOWED_CALLS_SCHEMA,
        allowed_addresses: ALLOWED_CALLS_SCHEMA,
      })
      .upgrade(async tx => {
        const allowedAddresses = await tx.table('allowed_addresses').toArray()
        await tx
          .table('allowed_calls')
          .bulkAdd(
            allowedAddresses.map(item =>
              this.writeChecksum(item, 'allowed_calls')
            )
          )
      })
    this.version(13)
      .stores({
        // eslint-disable-next-line unicorn/no-null
        allowed_addresses: null,
      })
      .upgrade(async () => {})
    this.version(14)
      .stores({
        hasSeenPermissionChangeWarning: 'profileAddress',
      })
      .upgrade(tx => {
        return tx
          .table('authorized_apps')
          .toCollection()
          .modify(app => {
            if (app['displayName'] === 'This Browser Extension') {
              app['displayName'] = UP_BROWSER_EXTENSION
            }
            if (app['name'] === 'This Browser Extension') {
              app['name'] = UP_BROWSER_EXTENSION
            }
          })
      })
    this.version(15)
      .stores({
        digitalAssets: 'assetAddress',
      })
      .upgrade(tx => {
        return tx
          .table('authorized_apps')
          .toCollection()
          .modify(app => {
            if (app['displayName'] === 'UP Extension') {
              app['displayName'] = UP_BROWSER_EXTENSION
            }
            if (app['name'] === 'UP Extension') {
              app['name'] = UP_BROWSER_EXTENSION
            }
          })
      })
    this.version(16)
      .upgrade(tx => {
        return tx
          .table('popupProfiles')
          .toCollection()
          .modify(tx => {
            if (tx['saved']) {
              if (tx['saved']['hash']) {
                tx['saved']['verification'] = {
                  data: tx['saved']['hash'],
                  method: SUPPORTED_VERIFICATION_METHOD_STRINGS.KECCAK256_UTF8,
                }
              }
              delete tx['saved']['hash']
            }
            if (tx['temporary']) {
              if (tx['temporary']['hash']) {
                tx['temporary']['verification'] = {
                  data: tx['temporary']['hash'],
                  method: SUPPORTED_VERIFICATION_METHOD_STRINGS.KECCAK256_UTF8,
                }
              }
              delete tx['temporary']['hash']
            }
          })
      })
      .upgrade(tx => {
        return tx
          .table('lastPopupState')
          .toCollection()
          .modify(tx => {
            if (tx['editProfile']) {
              if (tx['editProfile']['hash']) {
                tx['editProfile']['verification'] = {
                  data: tx['editProfile']['hash'],
                  method: SUPPORTED_VERIFICATION_METHOD_STRINGS.KECCAK256_UTF8,
                }
              }
              delete tx['editProfile']['hash']
            }
          })
      })
    this.version(17).stores({
      generateLSP23Data: 'controllerAddress',
    })
    this.version(18).upgrade(tx => {
      return tx
        .table('profiles')
        .toCollection()
        .modify(profile => {
          profile['controllerDirty'] = true
        })
    })
    this.version(19)
      .stores({
        controllers: '[profileAddress+controllerAddress],profileAddress',
        controllerCustomNames: '[profileAddress+controllerAddress]',
      })
      .upgrade(async tx => {
        const apps = await tx.table('authorized_apps').toArray()

        const migrationData = apps
          .filter(app => app.name && app.name.length > 0)
          .map(app => ({
            controllerAddress: app.address,
            profileAddress: app.profileAddress,
            name: app.name,
          }))

        await tx.table('controllerCustomNames').bulkAdd(migrationData)
      })
    this.version(20)
      .stores({
        // eslint-disable-next-line unicorn/no-null
        authorized_apps: null,
      })
      .upgrade(async () => {})
    this.version(21).stores({
      somethingWentWrongErrors: 'timestamp',
    })
    // Fix popupProfiles that have the temporary field set to an empty object
    // DEV-11610
    this.version(22).upgrade(async tx => {
      const popupProfiles = (await tx
        .table('popupProfiles')
        .toArray()) as PopupProfile[]
      const filteredPopupProfiles = popupProfiles.filter(
        popupProfile =>
          typeof popupProfile.temporary === 'object' &&
          Object.keys(popupProfile.temporary).length === 0
      )
      for (const popupProfile of filteredPopupProfiles) {
        popupProfile.temporary = undefined
        await tx.table('popupProfiles').put(popupProfile)
      }
    })
    // Profile.importOrigin field added
    this.version(23).upgrade(async () => {})
  }
}
